import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export type ThemeMode = 'lightTheme' | 'darkTheme';

const STORAGE_KEY = 'wfleet-theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly bodyId = 'setThemeBody';
  private currentTheme$ = new BehaviorSubject<ThemeMode>(this.getStoredTheme());

  constructor() {}

  /** Current theme as observable (lightTheme | darkTheme) */
  get theme$() {
    return this.currentTheme$.asObservable().pipe(distinctUntilChanged());
  }

  /** Current theme value */
  get currentTheme(): ThemeMode {
    return this.currentTheme$.value;
  }

  /** True when dark theme is active */
  get isDark(): boolean {
    return this.currentTheme$.value === 'darkTheme';
  }

  /** True when light theme is active */
  get isLight(): boolean {
    return this.currentTheme$.value === 'lightTheme';
  }

  /** Initialize theme from storage and apply to body (call once at app startup) */
  initTheme(): void {
    const theme = this.getStoredTheme();
    this.applyTheme(theme);
    if (this.currentTheme$.value !== theme) {
      this.currentTheme$.next(theme);
    }
  }

  /** Toggle between light and dark theme */
  toggleTheme(): ThemeMode {
    const next: ThemeMode = this.isDark ? 'lightTheme' : 'darkTheme';
    this.setTheme(next);
    return next;
  }

  /** Set theme and persist */
  setTheme(theme: ThemeMode): void {
    if (this.currentTheme$.value === theme) return;
    this.applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {}
    this.currentTheme$.next(theme);
  }

  private getStoredTheme(): ThemeMode {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      if (stored === 'darkTheme' || stored === 'lightTheme') return stored;
    } catch (_) {}
    return 'lightTheme';
  }

  private applyTheme(theme: ThemeMode): void {
    const body = document.getElementById(this.bodyId) || document.body;
    if (!body) return;
    const previous: ThemeMode = theme === 'darkTheme' ? 'lightTheme' : 'darkTheme';
    body.classList.add('wf-theme-switching');
    body.classList.add(theme);
    body.classList.remove(previous);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        body.classList.remove('wf-theme-switching');
      });
    });
  }
}
